import React from "react";
import { ButtonGroup, Button } from "react-bootstrap";
import PropTypes from "prop-types";

/**
 * TimeRangeButtons component for selecting historical data time ranges
 * 
 * Provides a button group for selecting time ranges: 24h, 7d, 30d, 1y
 * Visually indicates the currently selected range
 * 
 * @param {Object} props - Component props
 * @param {string} props.selected - Currently selected time range
 * @param {Function} props.onChange - Callback when time range changes
 */
const TimeRangeButtons = ({ selected, onChange }) => {
  const ranges = [
    { value: "24h", label: "24H" },
    { value: "7d", label: "7D" },
    { value: "30d", label: "30D" },
    { value: "1y", label: "1Y" }
  ];

  // Handle keyboard navigation with arrow keys
  const handleKeyDown = (e, currentIndex) => {
    let newIndex = currentIndex;
    
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : ranges.length - 1;
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      newIndex = currentIndex < ranges.length - 1 ? currentIndex + 1 : 0;
    } else {
      return; // Let other keys work normally (Tab, Enter, Space)
    }
    
    // Focus the new button
    const buttons = document.querySelectorAll('[data-time-range-button]');
    if (buttons[newIndex]) {
      buttons[newIndex].focus();
    }
  };

  return (
    <ButtonGroup role="group" aria-label="Time range selection">
      {ranges.map((range, index) => (
        <Button
          key={range.value}
          data-time-range-button
          variant={selected === range.value ? "primary" : "outline-primary"}
          onClick={() => onChange(range.value)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          aria-pressed={selected === range.value}
          aria-label={`${range.label} time range`}
        >
          {range.label}
        </Button>
      ))}
    </ButtonGroup>
  );
};

TimeRangeButtons.propTypes = {
  selected: PropTypes.oneOf(["24h", "7d", "30d", "1y"]).isRequired,
  onChange: PropTypes.func.isRequired
};

export default TimeRangeButtons;
